// Promos y código del teléfono en «Agrega accesorios», solo lectura: se cambian en el paso 04.
// Con un kit solo queda el código; sus promos van en «Promos del kit».
import { Sparkles, Tag } from 'lucide-react';
import type { PhonePromotionsSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { Button } from '@/kit';
import { useSalePromotionEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import { seedFromSession } from '../lib/applySelection';
import { accessoryOnlyIds } from '../lib/accessoryDiscount';
import { evaluatedPromotionName } from '../lib/giftPanel';
import { discountCodeBlockedCopy, droppedCodeCopy } from '../lib/promotionErrorCopy';
import { rememberedPromotionName } from '../lib/promotionNames';
import { choicesOn } from '../lib/promotionSelection';
import { useSessionPromotionState } from '../store/promotionSelectionStore';

export interface PhonePromotionsReadOnlyProps extends PhonePromotionsSlotContext {
    sessionId: string;
}

export function PhonePromotionsReadOnly({
    sessionId,
    onChangeInPhoneStep,
    ...context
}: PhonePromotionsReadOnlyProps) {
    const { anchorLine, quoteState } = context;
    const state = useSessionPromotionState(sessionId);
    const { data: evaluation } = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    const isKit = anchorLine.components.length > 0;
    const known = state.synced && state.selection.anchorLineId === anchorLine.lineId;
    const selection = known ? state.selection : seedFromSession(context);
    const code = known ? state.selection.code : (anchorLine.discountCode ?? null);
    // Las de solo accesorios van en su precio y los regalos por elegir, en «Regalos».
    const hidden = accessoryOnlyIds(evaluation, store?.items ?? []);
    const draft = state.phoneDraft?.sku === anchorLine.sku ? state.phoneDraft : undefined;
    for (const o of draft?.options ?? []) if (o.giftPending) hidden.add(o.promotionId);
    const names = choicesOn(selection, anchorLine.lineId)
        .filter((c) => !hidden.has(c.promotionId))
        .map(
            (c) =>
                evaluatedPromotionName(evaluation, c.promotionId, c.promotionVersion) ??
                rememberedPromotionName(sessionId, c.promotionId) ??
                'Promoción',
        )
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
    const blockedBy = quoteState === 'charged' ? null : state.discountCodeBlockedBy;
    const codeNotice = state.droppedCode ? droppedCodeCopy(state.droppedCode) : null;
    if (isKit && !code && !codeNotice && !blockedBy) return null;

    return (
        <section aria-label="Promos del teléfono" className="flex flex-col gap-1.5 text-[12px]">
            {!isKit && (
                <>
                    <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
                            <Sparkles className="size-3.5" aria-hidden />
                            Promos del teléfono
                        </span>
                        {quoteState !== 'charged' && (
                            <Button
                                size="sm"
                                variant="link"
                                className="h-auto p-0 text-[12px]"
                                onClick={onChangeInPhoneStep}
                            >
                                Cambiar en el paso 04
                            </Button>
                        )}
                    </div>
                    {names.length > 0 ? (
                        <ul className="flex flex-col gap-0.5 pl-5">
                            {names.map((name) => (
                                <li key={name}>{name}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="pl-5 text-muted-foreground">Sin promos.</p>
                    )}
                </>
            )}
            {code && (
                <p className="flex items-center gap-1.5">
                    <Tag className="size-3.5 text-muted-foreground" aria-hidden />
                    <span>
                        Código <strong>{code}</strong> aplicado
                    </span>
                </p>
            )}
            {!code && codeNotice && (
                <p role="status" className="text-warning">
                    {codeNotice}
                </p>
            )}
            {blockedBy && (
                <p role="status" className="text-muted-foreground">
                    {discountCodeBlockedCopy(blockedBy)}
                </p>
            )}
        </section>
    );
}
